// @spec SHL-STR-018
'use client';

import { CheckIcon } from 'lucide-react';
import * as React from 'react';

import { cn } from '@/lib/utils';

export interface SearchFilterChipProps {
  /** Chip label text */
  label: string;
  /** Whether chip is currently selected */
  isSelected: boolean;
  /** Called when chip is toggled */
  onToggle: () => void;
  /** Optional icon to show before label */
  icon?: React.ComponentType<{ className?: string | undefined }>;
}

/**
 * Individual filter chip with toggle behavior.
 *
 * Renders as a button with aria-pressed for accessibility.
 * Shows a checkmark icon when selected.
 *
 * @example
 * <SearchFilterChip
 *   label="Team Members"
 *   isSelected={selectedCategories.includes('members')}
 *   onToggle={() => toggleCategory('members')}
 * />
 */
export function SearchFilterChip({
  label,
  isSelected,
  onToggle,
  icon: Icon,
}: SearchFilterChipProps) {
  return (
    <button
      type="button"
      aria-pressed={isSelected}
      onClick={onToggle}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        isSelected
          ? 'bg-primary text-primary-foreground'
          : 'bg-muted text-muted-foreground hover:bg-muted/80'
      )}
    >
      {Icon && <Icon className="h-3 w-3" />}
      <span>{label}</span>
      {isSelected && <CheckIcon className="h-3 w-3" />}
    </button>
  );
}
